import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { useRouter } from 'next/router';
import AnchoredModal from '@components/Shared/Modal/AnchoredModal/AnchoredModal';
import TextField from '@components/Shared/TextField';

import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { getVerticalOffsetForOverlay } from './getVerticalOffset';
import { getAutoCompletedModels } from '@services/homesService';
import { AUTOCOMPLETE_SEARCH_MIN_CHARS } from '@utils/config';
import ModelList from '../../Shared/ModelList/ModelList';
import { FilterNameStyles } from './FilterName.styled';

const FilterName = ({ onCloseClick }) => {
  const [modelResults, setModelResults] = useState(null);
  //Starting selected index at -1 to not have it select anything on dropdown reveal.
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const router = useRouter();

  //Last search is compared to see if there any changes (only used to check length right now for delete key usage.)
  const [lastSearch, setLastSearch] = useState('');

  const textRef = useRef(null);
  const autoCompleteSearch = () => {
    const searchTerm = textRef.current.value;
    setLastSearch(searchTerm);

    getAutoCompletedModels(searchTerm).then((results) => {
      setModelResults(results);
    });
  };

  const clearSearch = () => {
    setModelResults(null);
    setSelectedIndex(-1);
  };

  const navigateToModel = (item) => {
    pushGTMEvent('filterNameClickModel');
    router.push(`/homes/${item.modelNumber}`);
  };

  const onKeyUp = (ev, value) => {
    //Get the Char Code for the selected state. We are doing this instead of ev.keyCode because of
    //how android virtual keyboards sometime act in situations, such as sending over a processing
    //key code every time. (KeyCode 229)
    let keyCode = ev.target.value
      .charAt(ev.target.selectionStart - 1)
      .charCodeAt();
    let inputValue = textRef.current.value;
    var eventKeyCode = String.fromCharCode(ev.keyCode);

    //Check if the value is a letter or number or if the value has changed. If the value hasn't changed,
    //let's ignore it!
    if (
      //Backspace or Delete Key
      ev.keyCode === 8 ||
      (ev.keyCode === 46 && lastSearch.length !== inputValue.length) ||
      ((typeof inputValue === 'undefined' || value !== inputValue) &&
        //Check if a valid character was typed.
        /[a-zA-Z0-9-_ \b]/.test(eventKeyCode) &&
        ((keyCode >= 48 && keyCode <= 57) ||
          (keyCode >= 65 && keyCode <= 90) ||
          (keyCode >= 97 && keyCode <= 122) ||
          keyCode === 32 ||
          keyCode === 189))
    ) {
      if (inputValue.length >= AUTOCOMPLETE_SEARCH_MIN_CHARS) {
        autoCompleteSearch();
      } else if (inputValue.length < AUTOCOMPLETE_SEARCH_MIN_CHARS) {
        clearSearch();
      }
    }
  };

  const onKeyDown = (ev) => {
    if (ev.keyCode === 13 && modelResults !== null) {
      let useIndex = selectedIndex !== -1 ? selectedIndex : 0;
      let selectedItem = modelResults[useIndex];

      if (selectedItem) {
        navigateToModel(selectedItem);
      }
    }

    //Arrow Up
    if (ev.keyCode === 38) {
      moveIndex(-1);
    }

    //Arrow Down
    if (ev.keyCode === 40) {
      moveIndex(1);
    }
  };

  const moveIndex = (i) => {
    if (modelResults !== null) {
      let temp = selectedIndex + i;

      if (temp < 0) {
        temp = 0;
      } else if (temp >= modelResults.length) {
        let listIndex = modelResults.length;
        temp = listIndex - 1;
      }

      setSelectedIndex(temp);
    }
  };

  let modelText =
    modelResults !== null && modelResults.length > 0 ? (
      <ModelList
        indexHighlighted={selectedIndex}
        autoCompleteOnSelection={(item) => {
          navigateToModel(item);
        }}
        modelResults={modelResults}
      />
    ) : null;

  /* eslint-disable react/jsx-no-duplicate-props */
  return (
    <AnchoredModal
      showClearButton={false}
      showApplyButton={false}
      onCloseClick={onCloseClick}
      automationId="filters-name-modal"
      pageOverlayVerticalOffsetInPixels={getVerticalOffsetForOverlay()}
    >
      <FilterNameStyles>
        <TextField
          id="modelName"
          textFieldStyle="compact"
          ref={textRef}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          label="Name or model #"
          automationId="account-creation-first-name-textfield"
        />
        {modelText}
      </FilterNameStyles>
    </AnchoredModal>
  );
  /* eslint-enable react/jsx-no-duplicate-props */
};

FilterName.propTypes = {
  onCloseClick: PropTypes.any,
};

export default FilterName;
